// src/shared/stores/createPatientStore.ts
import { createStore } from 'zustand/vanilla';

import { Agreement, Patient, PatientCreateRequest } from '../api/hooks/patient';

const TEMP_STORAGE_KEY = 'TEMP_USERS';
export const TEMP_USR_ID_PREFIX = 'TEMP_';

interface TempPatient extends Patient {
  orgId: number;
}

export interface PatientState {
  orgId: number;
  expanded: boolean;
  _users: Patient[];
  tempUsers: TempPatient[];
  _userId: string | null;
  agreements: Record<string, Agreement[]>;
  openEditDialog: boolean;
  openDeleteDialog: boolean;
  setOpenEditDialog: (open: boolean) => void;
  setOpenDeleteDialog: (open: boolean) => void;
  setUsers: (users?: Patient[]) => void;
  setUserAgreements: (patientId: string, agreements: Agreement[]) => void;
  deleteUser: (patientId: string) => void;
  updateUser: (user: Patient, editInfo?: boolean) => void;
  setUser: (user: Patient) => void;
  setUserId: (userId?: string) => void;
  addTempUser: (patient: PatientCreateRequest) => void;
  updateTempUserSendTime: (id: string, sendTime: Date) => void;
  removeTempUser: (id: string) => void;
  /** cross-tab storage 이벤트 반영용 (Provider에서 호출) */
  _setTempUsersFromStorage: (raw: string | null) => void;
}

const readTempUsers = (orgId: number): TempPatient[] => {
  try {
    const data = localStorage.getItem(TEMP_STORAGE_KEY);
    const arr = data ? (JSON.parse(data) as TempPatient[]) : [];
    return arr.filter((v) => v.orgId === orgId);
  } catch (e) {
    return [];
  }
};

const saveTempUsers = (orgId: number, users: TempPatient[]) => {
  try {
    const data = localStorage.getItem(TEMP_STORAGE_KEY);
    const arr = data ? (JSON.parse(data) as TempPatient[]) : [];
    const newUsers = [...arr.filter((v) => v.orgId !== orgId), ...users];
    localStorage.setItem(TEMP_STORAGE_KEY, JSON.stringify(newUsers));
  } catch (e) {
    localStorage.setItem(TEMP_STORAGE_KEY, JSON.stringify(users));
  }
};

export const createPatientStore = (orgId = 0) =>
  createStore<PatientState>((set, get) => ({
    orgId,
    expanded: false,
    _users: [],
    tempUsers: readTempUsers(orgId),
    _userId: null,
    agreements: {},
    openEditDialog: false,
    openDeleteDialog: false,

    setOpenEditDialog: (open) => set({ openEditDialog: open }),
    setOpenDeleteDialog: (open) => set({ openDeleteDialog: open }),

    setUsers: (users = []) => {
      const uniqueKeys = users.map((v) => `${v.code}-${v.phoneNumber}`);
      const isPromoted = (v: TempPatient) => uniqueKeys.includes(`${v.code}-${v.phoneNumber}`);
      const promotedTempUsers = get().tempUsers.filter(isPromoted);
      const tempUsers = get().tempUsers.filter((v) => !isPromoted(v));
      saveTempUsers(get().orgId, tempUsers);

      // 선택 중이던 temp가 real로 승격돼 목록에서 제거되면, 선택을 매칭되는 real 사용자로 이관한다.
      // (socket USER_AGREED 경로 외에 리스트 refetch로만 승격되는 경우 선택이 유실되던 문제)
      let _userId = get()._userId;
      const selectedPromotedTemp = promotedTempUsers.find((v) => v.id === _userId);
      if (selectedPromotedTemp) {
        const realUser = users.find(
          (u) => u.code === selectedPromotedTemp.code && u.phoneNumber === selectedPromotedTemp.phoneNumber,
        );
        if (realUser) {
          _userId = realUser.id;
        }
      }
      set({ _users: users, tempUsers, _userId });
    },

    setUserAgreements: (patientId, agreements) =>
      set({ agreements: { ...get().agreements, [patientId]: agreements } }),

    deleteUser: (patientId) => {
      const _users = get()._users.filter((u) => u.id !== patientId);
      let _userId = get()._userId;
      if (_userId === patientId) {
        _userId = _users.length > 0 ? _users[0].id : null;
      }
      set({ _users, _userId });
    },

    updateUser: (user, editInfo = false) => {
      if (editInfo) {
        const cur = [...get()._users];
        const idx = cur.findIndex((u) => u.id === user.id);
        if (idx > -1) {
          cur[idx] = { ...user };
          set({ _users: [...cur] });
        }
      } else {
        const _users = [user, ...get()._users.filter((u) => u.id !== user.id)];
        const tempUser = get().tempUsers.find((v) => v.code === user.code && v.phoneNumber === user.phoneNumber);
        if (tempUser) {
          const tempUsers = get().tempUsers.filter((v) => v.id !== tempUser.id);
          saveTempUsers(get().orgId, tempUsers);
          const _userId = get()._userId === tempUser.id ? user.id : get()._userId;
          set({ _users, tempUsers, _userId });
        } else {
          set({ _users });
        }
      }
    },

    setUser: (user) => {
      set({ _userId: user.id });
    },

    setUserId: (userId) => set({ _userId: userId ?? null }),

    addTempUser: (patient) => {
      const tempId = `${TEMP_USR_ID_PREFIX}${Date.now()}`;
      const temp = {
        ...patient,
        orgId: patient.orgId,
        id: tempId,
        userUid: tempId,
        createdTime: new Date().toISOString(),
        modifiedTime: new Date().toISOString(),
      } as TempPatient;
      const tempUsers = [temp, ...get().tempUsers];
      saveTempUsers(get().orgId, tempUsers);
      set({ tempUsers });
    },

    updateTempUserSendTime: (id, sendTime) => {
      const tempUsers = [...get().tempUsers];
      const idx = tempUsers.findIndex((v) => v.id === id);
      if (idx > -1) {
        tempUsers[idx] = { ...tempUsers[idx], createdTime: sendTime.toISOString() };
        saveTempUsers(get().orgId, tempUsers);
        set({ tempUsers });
      }
    },

    removeTempUser: (id) => {
      const tempUsers = get().tempUsers.filter((v) => v.id !== id);
      let _userId = get()._userId;
      if (_userId === id) {
        _userId = tempUsers.length > 0 ? tempUsers[0].id : null;
      }
      saveTempUsers(get().orgId, tempUsers);
      set({ tempUsers, _userId });
    },

    _setTempUsersFromStorage: (raw) => {
      try {
        const all = JSON.parse(raw || '[]') as TempPatient[];
        // 다른 탭의 storage 이벤트는 전체 org tempUsers를 담으므로 현재 org로 필터해 per-org 격리를 지킨다.
        set({ tempUsers: all.filter((v) => v.orgId === get().orgId) });
      } catch (e) {
        console.warn('[PatientStore] storage 이벤트 JSON 파싱 실패', e);
      }
    },
  }));

export type PatientStoreApi = ReturnType<typeof createPatientStore>;

// 파생값 (구 MobX getter 대체)
export const selectUsers = (s: PatientState): Patient[] => [...s._users, ...s.tempUsers];
export const selectTempPatients = (s: PatientState): Patient[] => s.tempUsers;
export const selectUser = (s: PatientState): Patient | undefined => {
  const all = [...s._users, ...s.tempUsers];
  if (s._userId) {
    const found = all.find((v) => v.id === s._userId);
    if (found) return found;
  }
  // _userId 미설정이거나 존재하지 않는(stale) id면 첫 사용자(서버 우선, 없으면 temp)로 폴백한다.
  return all[0];
};
export const selectUserAgreements = (s: PatientState): Agreement[] | undefined => {
  const u = selectUser(s);
  return u ? s.agreements[u.id] : undefined;
};
export const selectIsStimHomeAgreed = (s: PatientState): boolean =>
  !!selectUserAgreements(s)?.find((v) => v.type === 'STIM_HOME');
export const selectIsExpiredCommonAgree = (s: PatientState): boolean | undefined =>
  selectUserAgreements(s)?.find((v) => v.serviceType === 'THIRD_PARTY')?.expired ||
  selectUserAgreements(s)?.find((v) => v.serviceType === 'SENSITIVE_INFO')?.expired;
export const selectIsStimAgreed = (s: PatientState): boolean =>
  !!selectUserAgreements(s)?.find((v) => v.serviceType === 'STIM');
export const selectIsTempUser = (s: PatientState): boolean | undefined =>
  selectUser(s)?.id.startsWith(TEMP_USR_ID_PREFIX);
